import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface PhotoTutorialModalProps {
  open: boolean
  onClose: () => void
}

type IconKey = 'light' | 'clean' | 'background' | 'align' | 'shots' | 'defects' | 'check'

interface TutorialStep {
  icon: IconKey
  title: string
  body: string
  tip?: string
}

function StepIcon({ name }: { name: IconKey }) {
  const common = {
    width: 26,
    height: 26,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true as const,
  }
  const paths: Record<IconKey, ReactNode> = {
    light: (
      <>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
      </>
    ),
    clean: (
      <>
        <path d="M3 21l3-3" />
        <path d="M14.5 5.5l4 4L9 19H5v-4z" />
        <path d="M13 7l4 4" />
      </>
    ),
    background: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 15l5-5 4 4 3-3 6 6" />
      </>
    ),
    align: (
      <>
        <rect x="7" y="3" width="10" height="18" rx="2" />
        <path d="M12 3v18" strokeDasharray="2 2" />
      </>
    ),
    shots: (
      <>
        <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
        <circle cx="12" cy="13" r="3.2" />
      </>
    ),
    defects: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.3-4.3" />
        <path d="M11 8v3M11 14h.01" />
      </>
    ),
    check: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M8 12l2.5 2.5L16 9" />
      </>
    ),
  }
  return <svg {...common}>{paths[name]}</svg>
}

const STEPS: TutorialStep[] = [
  {
    icon: 'light',
    title: '1. Pasirinkite gerą apšvietimą',
    body: 'Geriausia natūrali dienos šviesa prie lango. Venkite tiesioginių saulės spindulių — jie sukurs šešėlius ir blizgesius.',
    tip: 'Ryto arba popietės šviesa yra švelnesnė nei vidurdienio.',
  },
  {
    icon: 'clean',
    title: '2. Paruoškite telefoną',
    body: 'Nuvalykite ekraną ir korpusą minkšta servetėle. Pirštų atspaudai ir dulkės iškart matomi nuotraukoje.',
    tip: 'Mikropluošto servetėlė — geriausias draugas.',
  },
  {
    icon: 'background',
    title: '3. Naudokite paprastą foną',
    body: 'Geriausia balta arba šviesi pilka plokštė / lapas. Venkite raštuotų paviršių, kurie atitraukia dėmesį.',
    tip: 'Galite naudoti baltą lapą popieriaus — to visiškai pakanka.',
  },
  {
    icon: 'align',
    title: '4. Laikykite telefoną tiesiai',
    body: 'Fotografuokite iš tiesioginio rakurso — be perspektyvos iškraipymo. Telefonas turi būti pilnai kadre, su mažomis paraštėmis aplink.',
    tip: 'Naudokite tinklelį (Grid) kameros nustatymuose, kad išlygiuotumėte.',
  },
  {
    icon: 'shots',
    title: '5. Padarykite visus reikalingus kadrus',
    body: 'Privalomi kadrai: priekis (su įjungtu ekranu), galas, abu šonai, viršus, apačia. Atskirai užfiksuokite bet kokius defektus.',
    tip: 'Nepamirškite kameros, IMEI lipduko, kroviklio jungties.',
  },
  {
    icon: 'defects',
    title: '6. Užfiksuokite defektus',
    body: 'Jei yra įbrėžimų, dėmių ar lūžių — sąžiningai juos parodykite iš arti. Pirkėjai vertina sąžiningumą labiau nei tobulą išvaizdą.',
    tip: 'Galite parodyti defektą su monetos ar pirštu šalia masto.',
  },
  {
    icon: 'check',
    title: '7. Patikrinkite kokybę',
    body: 'Prieš įkeldami — peržiūrėkite nuotraukas. Aiškios? Be neryškumo? Tinkamai apšviestos? Jei abejojate — perfotografuokite.',
    tip: 'Mūsų AI sistema automatiškai vertins kokybę.',
  },
]

const EXAMPLES = [
  {
    label: 'GERAI',
    desc: 'Balta plokštė, ryški dienos šviesa, telefonas tiesiai kadre',
    color: '#10b981',
  },
  {
    label: 'BLOGAI',
    desc: 'Tamsi nuotrauka, raštuotas fonas, telefonas pakreiptas',
    color: '#ef4444',
  },
]

export default function PhotoTutorialModal({ open, onClose }: PhotoTutorialModalProps) {
  const [step, setStep] = useState(0)
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const scrollY = window.scrollY
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    return () => {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      window.scrollTo(0, scrollY)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    bodyRef.current?.scrollTo(0, 0)
  }, [open, step])

  if (!open) return null

  const current = STEPS[step]
  const isLast = step === STEPS.length - 1

  function handleClose() {
    setStep(0)
    onClose()
  }

  return createPortal(
    <div className="photo-tutorial-backdrop" role="presentation" onClick={handleClose}>
      <div
        className="photo-tutorial"
        role="dialog"
        aria-modal="true"
        aria-labelledby="photo-tutorial-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="photo-tutorial__close"
          onClick={handleClose}
          aria-label="Uždaryti"
        >
          ×
        </button>

        <header className="photo-tutorial__head">
          <span className="photo-tutorial__pill">Pamoka — kaip fotografuoti telefoną</span>
          <h2 id="photo-tutorial-title">Profesionali nuotrauka per 7 žingsnius</h2>
        </header>

        <div className="photo-tutorial__progress" aria-hidden="true">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`photo-tutorial__progress-dot${i === step ? ' photo-tutorial__progress-dot--active' : ''}${i < step ? ' photo-tutorial__progress-dot--done' : ''}`}
            />
          ))}
        </div>

        <div className="photo-tutorial__body" ref={bodyRef}>
          <article className="photo-tutorial__step">
            <div className="photo-tutorial__step-head">
              <div className="photo-tutorial__icon" aria-hidden="true">
                <StepIcon name={current.icon} />
              </div>
              <h3>{current.title}</h3>
            </div>
            <p>{current.body}</p>
            {current.tip ? (
              <div className="photo-tutorial__tip">
                <span className="photo-tutorial__tip-label">Patarimas</span>
                {current.tip}
              </div>
            ) : null}
          </article>

          {isLast ? (
            <div className="photo-tutorial__examples">
              {EXAMPLES.map((ex) => (
                <div key={ex.label} className="photo-tutorial__example" style={{ borderColor: ex.color }}>
                  <span className="photo-tutorial__example-label" style={{ color: ex.color }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      {ex.label === 'GERAI' ? <path d="M5 12l5 5L20 7" /> : <path d="M6 6l12 12M18 6L6 18" />}
                    </svg>
                    {ex.label}
                  </span>
                  <p>{ex.desc}</p>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <footer className="photo-tutorial__foot">
          <button
            type="button"
            className="photo-tutorial__nav photo-tutorial__nav--back"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
          >
            ← Atgal
          </button>
          <span className="photo-tutorial__counter">{step + 1} / {STEPS.length}</span>
          {!isLast ? (
            <button
              type="button"
              className="photo-tutorial__nav photo-tutorial__nav--next"
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
            >
              Toliau →
            </button>
          ) : (
            <button type="button" className="photo-tutorial__nav photo-tutorial__nav--done" onClick={handleClose}>
              Supratau
            </button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  )
}
